import { useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/base/card';
import { Input } from '@/components/base/input';
import { Label } from '@/components/base/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/base/alert';
import { explorerTxUrl, sendSolTransfer } from '@/solana';
import { useAccount } from '@/state/account';

export function SendSolCard() {
  const { account, network, connection, pushTx, refreshBalances } = useAccount();
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!account) return null;

  const valid = recipient.trim().length > 0 && parseFloat(amount) > 0 && Number.isFinite(parseFloat(amount));

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    setError(null);
    setSignature(null);
    try {
      const sig = await sendSolTransfer(
        connection,
        account.scalar,
        account.pubkey,
        recipient.trim(),
        parseFloat(amount),
      );
      setSignature(sig);
      pushTx({ kind: 'sol', signature: sig, network });
      setAmount('');
      void refreshBalances();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to send SOL.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="para:flex para:items-center para:gap-2">
          <Send className="para:size-5" />
          Send SOL
        </CardTitle>
      </CardHeader>
      <CardContent className="para:flex para:flex-col para:gap-3">
        <div className="para:flex para:flex-col para:gap-2">
          <Label htmlFor="sol-recipient">Recipient address</Label>
          <Input
            id="sol-recipient"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="Solana address"
            spellCheck={false}
          />
        </div>
        <div className="para:flex para:flex-col para:gap-2">
          <Label htmlFor="sol-amount">Amount (SOL)</Label>
          <Input
            id="sol-amount"
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Transfer failed</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {signature && (
          <Alert>
            <AlertTitle>Transaction submitted</AlertTitle>
            <AlertDescription>
              <a
                href={explorerTxUrl(signature, network)}
                target="_blank"
                rel="noreferrer"
                className="para:underline para:break-all"
              >
                {signature}
              </a>
            </AlertDescription>
          </Alert>
        )}
        <Button onClick={submit} disabled={!valid || busy} isLoading={busy}>
          Send
        </Button>
      </CardContent>
    </Card>
  );
}
